import { ErrandResponse } from '@backend/schemas/src/errands'
import { ServiceGroupWithGenerationResponse } from '@backend/schemas/src/service-groups'

export type ErrandServiceGroup = ServiceGroupWithGenerationResponse & {
  chosenServices: ServiceGroupWithGenerationResponse['services']
}

/**
 * Cross-references an errand's chosen service groups/services (id references only) against the
 * full service-group data (descriptions, links, AI assessment) already fetched by `useServiceGroups`
 * — the same data `services.tsx` renders — so the errand page can show the same grouped/expandable
 * view instead of a flat, assessment-less list.
 */
export const getErrandServiceGroups = (
  errand: Pick<ErrandResponse, 'serviceGroups'>,
  allServiceGroups: ServiceGroupWithGenerationResponse[],
): ErrandServiceGroup[] =>
  (errand.serviceGroups ?? []).flatMap(entry => {
    const full = allServiceGroups.find(group => group.id === entry.serviceGroup.id)
    if (!full) return []

    const chosenIds = new Set((entry.services ?? []).map(s => s.serviceId))

    return [{ ...full, chosenServices: full.services.filter(s => chosenIds.has(s.id)) }]
  })

/**
 * Flat, ordered list of the service titles chosen on an errand — e.g. for a one-line summary.
 * Reads straight off the errand's own embedded service-group data, so unlike
 * `getErrandServiceGroups` it needs no `useServiceGroups` fetch to cross-reference against.
 */
export const getErrandChosenServiceTitles = (errand: Pick<ErrandResponse, 'serviceGroups'>): string[] =>
  (errand.serviceGroups ?? []).flatMap(entry => {
    const chosenIds = new Set((entry.services ?? []).map(s => s.serviceId))
    return entry.serviceGroup.services.filter(s => chosenIds.has(s.id)).map(s => s.title)
  })
